import React, { useState, useEffect } from 'react';
import { Menu, Plus } from 'lucide-react';
import { themeFilters, contentTypeFilters } from '../utils/filters';

const MobileHeader = ({ 
  isDarkMode, 
  setIsDarkMode, 
  setShowAddForm, 
  activeFilters,
  setActiveFilters 
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Update project management label
  const updatedThemeFilters = themeFilters.map(filter => 
    filter.type === "Project management" 
      ? { ...filter, type: "Algemeen" } 
      : filter
  );

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <>
      {/* Sliding Menu */}
      <div 
        className={`fixed inset-0 z-50 transition-transform duration-300 ${
          isMenuOpen ? 'transform translate-x-0' : 'transform -translate-x-full'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50"
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu Content */}
        <div className={`relative w-72 h-full ${
          isDarkMode ? 'bg-[#282828]' : 'bg-white'
        } p-6 overflow-y-auto`}>
          <div className="space-y-8">
            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between">
              <span className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Dark mode
              </span>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`w-12 h-6 rounded-full relative ${
                  isDarkMode ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full transition-transform ${
                  isDarkMode ? 'right-1 bg-white' : 'left-1 bg-white'
                }`} />
              </button>
            </div>

            {/* Relevance Section */}
            <div className="space-y-4">
              <h3 className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Relevantie
              </h3>
              <div className="space-y-2">
                {updatedThemeFilters.map(({ type, Icon }) => (
                  <button
                    key={type}
                    onClick={() => {
                      setActiveFilters(prev => ({
                        ...prev,
                        theme: prev.theme.includes(type)
                          ? prev.theme.filter(t => t !== type)
                          : [...prev.theme, type]
                      }));
                    }}
                    className={`
                      w-full p-4 rounded-lg text-left transition-all flex items-center gap-3
                      relative overflow-hidden
                      ${isDarkMode 
                        ? 'bg-[#383838] text-gray-300 hover:bg-[#484848]'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                      ${activeFilters.theme.includes(type) &&
                        'before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-2.5 before:bg-[#e8c2d9]'
                      }
                    `}
                  >
                    <Icon size={24} />
                    <span className="text-lg">{type}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Type Section */}
            <div className="space-y-4">
              <h3 className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Type informatie
              </h3>
              <div className="space-y-2">
                {contentTypeFilters.map(({ type, Icon }) => (
                  <button
                    key={type}
                    onClick={() => {
                      setActiveFilters(prev => ({
                        ...prev,
                        contentType: prev.contentType.includes(type)
                          ? prev.contentType.filter(t => t !== type)
                          : [...prev.contentType, type]
                      }));
                    }}
                    className={`
                      w-full p-4 rounded-lg text-left transition-all flex items-center gap-3
                      relative overflow-hidden
                      ${isDarkMode 
                        ? 'bg-[#383838] text-gray-300 hover:bg-[#484848]'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                      ${activeFilters.contentType.includes(type) &&
                        'before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-2.5 before:bg-[#e8c2d9]'
                      }
                    `}
                  >
                    <Icon size={24} />
                    <span className="text-lg">{type}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div
        className={`mobileheader fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${
          isVisible
            ? 'transform translate-y-0'
            : 'transform translate-y-full'
        } ${isDarkMode ? 'bg-[#282828]' : 'bg-white'}`}
      >
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setIsMenuOpen(true)}
            className={`p-2 rounded-lg ${
              isDarkMode
                ? 'bg-[#383838] text-gray-300 hover:bg-[#484848]'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Menu size={24} />
          </button>

          <img
            src="https://www.unframed.nl/app/uploads/2025/01/mobile-logo.png"
            alt="Unframed AI"
            className="h-8 w-auto"
          />

          <button
            onClick={() => setShowAddForm(true)}
            className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus size={24} />
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileHeader;